/**
 * UI wrappers around the shared PRD role-assignment matrix.
 * Prefer importing from `@unify/fhir-model/role-assignment-matrix` for new code.
 */

import {
  authorizeRoleChange,
  canEditorAssignRole as canEditorAssignUnifyRole,
  getAssignableRolesForEditor as getAssignableUnifyRolesForEditor,
  isUserRoleFieldEditable as isUnifyUserRoleFieldEditable,
} from '@unify/fhir-model/role-assignment-matrix';
import type { Role } from '../../pages/users/types/rbac';

/** Roles an editor may assign when changing another user's primary role. */
export function getAssignableRolesForEditor(
  editorRole: Role,
  targetRole: Role,
): Role[] {
  if (targetRole === 'client' || editorRole === 'client') {
    return targetRole === 'client' ? ['client'] : [];
  }
  return getAssignableUnifyRolesForEditor(editorRole, targetRole) as Role[];
}

/** Highest-privilege held role for actor-matrix checks (never session active). */
const ASSIGNMENT_ACTOR_PRIORITY: Role[] = [
  'unify-admin',
  'practice-owner',
  'practice-admin',
  'supervisor',
  'provider',
];

export function getAssignmentActorRole(held: readonly Role[], fallback: Role): Role {
  for (const role of ASSIGNMENT_ACTOR_PRIORITY) {
    if (held.includes(role)) return role;
  }
  return fallback;
}

/** Union of assignable roles across every held editor role. */
export function getAssignableRolesForHeldRoles(
  held: readonly Role[],
  targetCurrentRole: Role,
): Role[] {
  const editors = held.length ? held : [targetCurrentRole];
  const set = new Set<Role>();
  for (const editor of editors) {
    for (const role of getAssignableRolesForEditor(editor, targetCurrentRole)) {
      set.add(role);
    }
  }
  return [...set];
}

/**
 * Whether the editor may change this user's role.
 * Self-assignment is allowed when the actor may assign roles (PRD rule 3).
 */
export function isUserRoleFieldEditable(
  editorRole: Role,
  targetRole: Role,
  isSelf: boolean,
): boolean {
  if (editorRole === 'client' || targetRole === 'client') return false;
  return isUnifyUserRoleFieldEditable(editorRole, targetRole, isSelf);
}

/** Whether a specific role is a valid assignment for this editor. */
export function canEditorAssignRole(editorRole: Role, role: Role): boolean {
  if (role === 'client' || editorRole === 'client') return false;
  return canEditorAssignUnifyRole(editorRole, role);
}

/** Full check used before invite / set-role / role-transition. */
export function canChangeUserRole(input: {
  editorRole: Role;
  targetCurrentRole?: Role;
  targetNewRole: Role;
  isSelf: boolean;
}): boolean {
  if (input.targetNewRole === 'client' || input.editorRole === 'client') return false;
  return authorizeRoleChange({
    editorRole: input.editorRole,
    targetCurrentRole: input.targetCurrentRole,
    targetNewRole: input.targetNewRole,
    isSelf: input.isSelf,
  }).allowed;
}
